<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetInvoiceParty',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    path: {
      type: String,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onReferenceInvoiceSelected = (...args) => {
    const { option, value } = args[0];

    return selectClient({ clientId: option.client.id });
  };
  const selectClient = (...args) => {
    const { clientId } = args[0];
    return $fn.value
      .fetch('get_invoice_party_details', { parameters: { id: clientId } })
      .then((data) => setData('invoice.client', data));
  };
  const selectCompany = (...args) => {
    const { companyId } = args[0];
    return $fn.value
      .fetch('get_invoice_party_details', { parameters: { id: companyId } })
      .then((data) => setData('invoice.company', data));
  };

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    return false;
  });
  const item = computed(() => {
    return props.invoice[props.path];
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.path,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('path');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div v-if="loaded">
    <div class="form-group">
      <ItpFormInput
        :autofocus="!isReadonly"
        name="name"
        v-model="item.name"
        :placeholder="pgettext('InvoiceParty', 'Name')"
        :readonly="isReadonly"
        required
      >
      </ItpFormInput>
    </div>
    <div class="row" v-if="item.address">
      <div class="form-group col-2">
        <ItpFormInput
          name="postal_code"
          v-model="item.address.postal_code"
          :placeholder="pgettext('InvoiceParty', 'Postal code')"
          :readonly="isReadonly"
        >
        </ItpFormInput>
      </div>
      <div class="form-group col-10">
        <ItpFormInput
          name="city"
          v-model="item.address.city"
          :placeholder="pgettext('InvoiceParty', 'City')"
          :readonly="isReadonly"
        >
        </ItpFormInput>
      </div>
    </div>
    <div class="form-group" v-if="item.address">
      <ItpFormInput
        name="address"
        v-model="item.address.address"
        :placeholder="pgettext('InvoiceParty', 'Address')"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </div>
    <div class="form-group">
      <ItpFormInput
        name="tax_number"
        v-model="item.tax_number"
        :placeholder="pgettext('InvoiceParty', 'Tax number')"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </div>
    <div class="form-group">
      <ItpFormInput
        name="account_number"
        v-model="item.account_number"
        :placeholder="pgettext('InvoiceParty', 'Account number')"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </div>
    <div class="form-group">
      <ItpFormInput
        name="bank"
        v-model="item.bank"
        :placeholder="pgettext('InvoiceParty', 'Bank')"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </div>
  </div>
</template>
